<template>
  <div class="login">
    <div class="login__wrapper">
      <Logo />
      <q-form @submit.prevent="onSubmit" class="login__form" :class="{ 'login__form--loading': loading }">
        <BackBtn v-if="showCodeInput" @click="onBack" />
        <CustomInput
          class="login__form_item q-mb-sm"
          :value="email"
          @change_input="(val) => email = val"
          type="email"
          label="Электронная почта"
          :required="true"
          :hideBottomSpace="false"
          lazy-rules
          :rules="[(val) => (val && val.length > 0) || 'Введите email']"
        />
        <div class="flex column items-end relative-position">
          <router-link :to="pages.forgetPassword" class="login__link">
            <p class="login__forget">Забыли пароль?</p>
          </router-link>
          <CustomInput
            class="login__form_item q-mb-sm"
            type="password"
            :value="password"
            :required="true"
            :hideBottomSpace="false"
            @change_input="(val) => password = val"
            label="Пароль"
            lazy-rules
            :rules="[(val) => (val && val.length > 0) || 'Введите пароль']"
          />
        </div>

        <div v-if="showCodeInput" class="login__form_itemCode q-mb-sm">
          <CustomInput
            class="login__form_item"
            :value="code_key"
            @change_input="(val) => code_key = val"
            label="Код *"
            input-class="login__form_itemInput"
            lazy-rules
            type="number"
            :rules="[(val) => (val && val.length > 0) || 'Введите код']"
          />
          <q-btn
            color="secondary"
            :loading="codeLoading"
            :disable="codeLoading"
            class="login__form_itemCodeBtn"
            @click="sendCodeData"
          >
            <div>
              Получить код
              <q-icon
                name="fa-solid fa-paper-plane"
                class="login__form_itemIcon"
              />
            </div>
          </q-btn>
        </div>
        <div class="flex justify-center items-center">
          <CustomButton
            class="login__submit"
            type="submit"
            color="primary-light"
            :disable="loading || btnDisabled"
            :loading="loading"
          >
            {{ loading && btnDisabled ? 'Вход выполнен...' : 'Войти' }}
          </CustomButton>
        </div>
      </q-form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { doLogin, sendLoginCode } from "./services";
import { PAGES } from "@/constants";
import CustomInput from "@/components/form/CustomInput";
import CustomButton from "@/components/common/CustomButton";
import { Notify } from "quasar";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import BackBtn from "@/components/common/BackBtn";
import Logo from "./Logo.vue";
import { storeToRefs } from "pinia";
import { useAppStore } from "@/services/store/app";

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();
const { showSucceedSnackbar } = useCustomSnackbar();
const { keycloakError: storeKeycloakError } = storeToRefs(appStore);

const loading = ref(false);
const btnDisabled = ref(false);
const codeLoading = ref(false);
const showCodeInput = ref(false);
const email = ref("");
const password = ref("");
const code_key = ref("");

const redirectDest = computed(() => route.query?.redirect || "");

const pages = computed(() => PAGES);
function showKeycloakError(message) {
  Notify.create({
    type: "negative",
    position: "bottom-left",
    timeout: 10000,
    message,
  });
  nextTick(() => appStore.clearKeycloakError());
}

onMounted(async () => {
  if (appStore.auth) {
    router.push("/");
    return;
  }
  const message = storeKeycloakError.value || route.query?.keycloak_error;
  if (message) showKeycloakError(message);
});

watch(
  () => storeKeycloakError.value || route.query?.keycloak_error,
  (val) => {
    if (val) showKeycloakError(val);
  }
);

const onSubmit = async () => {
  if (loading.value) return;
  const body = { login: email.value, password: password.value };
  if (code_key.value && showCodeInput.value) {
    body.code_key = code_key.value;
  }
  loading.value = true;
  try {
    const res = await doLogin(body);
    if (res) {
      if (res.is_device_exist === false) {
        showCodeInput.value = true;
        return;
      }
      btnDisabled.value = true;
      appStore.setAuth();
      await appStore.getUserData();
      await appStore.getCitiesData();
      router.push(redirectDest.value || '/');
    }
  } finally {
    loading.value = false;
  }
};

const sendCodeData = async () => {
  const body = {
    login: email.value,
    password: password.value,
  };
  codeLoading.value = true;
  const res = await sendLoginCode(body);
  codeLoading.value = false;
  if (res) showSucceedSnackbar("Код был отправлен на вашу почту");
};

const onBack = () => {
  showCodeInput.value = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.login {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, .3);

  &__wrapper {
    width: 440px;
    background-color: #f5f5f5;
    padding: 60px 35px;
    height: auto;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border-radius: 16px;
  }

  &__form {
    width: 100%;
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    gap: 15px;

    &_item {
      margin-top: 0;
      margin-left: 0;
      width: 100%;
      border-radius: 4px !important;

      &Code {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        gap: 15px;

        &Btn {
          height: 40px;
          border-radius: 8px;
          font-size: 12px;
          white-space: nowrap;

          & > div {
            display: flex;
            align-items: center;
            gap: 5px;
            justify-content: space-between;
          }
        }
      }

      &Icon {
        font-size: 15px !important;
        margin-left: 3px;
      }
    }
  }

  &__submit {
    width: 200px;
    height: 45px;
    margin-left: 0;
    margin-top: 5px;
  }

  &__forget {
    color: $primary-light;
    font-size: 14px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    position: absolute;
    right: 0;
    top: -5px;

    &:hover {
      color: $primary-light;
      text-decoration: underline;
    }
  }

  &__link {
    text-decoration: none;
  }

  &__sso {
    width: 100%;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    border-top: 1px solid #e0e0e0;
    padding-top: 20px;

    &_btn {
      width: 220px;
      height: 45px;
      margin: 0;
    }

    &_links {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 13px;

      &-title {
        color: #777;
      }
    }

    &_link {
      color: $primary-light;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__form--loading {
    pointer-events: none;

    .login__link {
      pointer-events: none;
      opacity: 0.7;
    }
  }
}
</style>
